// per docs/shape-uc27-alert-engine.md §Implementation plan §11
// per docs/shape-uc-27-alert-delivery-legibility.md §5
// per docs/caiet-uc-spec.md §UC27-02 §UC-ALRT-07 §REUSE-03
// The channel register. Editing lives at its own address (`alert-channels/new`,
// `alert-channels/:id/edit`, NotificationChannelFormPage), as the wireframe's
// `canale/nou` and `canale/:id/edit` do (AdminAlerte.tsx:2717-2833).
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import type { TableColumn, TableRowData } from '@egov-moldova/mud/loader';
import { MudButton, MudIcon, MudInfoBox, MudTable, MudTag } from '@egov-moldova/mud-react';
import { apiClient, apiErrorKey } from '../../app/api-client';
import { QueryState } from '../../components/QueryState/QueryState';
import {
  EMPTY_CELL,
  EmptyRegisterMessage,
  MetadataLine,
  onRowOpen,
  PageHeader,
  RouterLink,
  RowActionsMenu,
  useToast,
} from '@simsm/ui-shared';
import { AlertsSectionNav } from '../../components/AlertsSectionNav';
import { AlertDefinitionsMenu } from '../../components/AlertDefinitionsMenu';
import { useConfirm } from '../../components/ConfirmDialog/ConfirmDialog';
import { useRoleLabel } from '../../hooks/use-observer-roles';

export type ChannelKind = 'email' | 'webhook' | 'inapp' | 'mnotify';

export interface ChannelDto {
  id: string;
  name: string;
  displayName: string;
  kind: ChannelKind;
  config: Record<string, unknown>;
  active: boolean;
}

export interface ChannelFormState {
  id: string | null;
  name: string;
  displayName: string;
  kind: ChannelKind;
  config: Record<string, string>;
  active: boolean;
}

// Keys the gateway redacts (sent as '__set__' when present, '' when unset).
const SECRET_KEYS = ['secret', 'smtp_password', 'api_key', 'password', 'token'];

// MNotify addresses an organization by IDNO and a person by IDNP, and which one
// is a property of the firing, not of the channel. So the channel stores the
// result field that carries the address; analytics-service reads it per event.
// The three options are the wireframe's (AdminAlerte.tsx MNOTIFY_RECIPIENTS).
export const MNOTIFY_RECIPIENT_LABELS = [
  'registrar_idno',
  'operator_idnp',
  'registrar_idno,operator_idnp',
] as const;

export const MNOTIFY_RECIPIENT_KEYS: Record<string, string> = {
  registrar_idno: 'alerts.channels.recipient.idno',
  operator_idnp: 'alerts.channels.recipient.idnp',
  'registrar_idno,operator_idnp': 'alerts.channels.recipient.idno_or_idnp',
};

export function kindLabelKey(kind: string): string {
  return `alerts.channels.kind.${kind === 'inapp' ? 'in_app' : kind}`;
}

export function configFromDto(dto: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(dto)) {
    if (v == null) continue;
    // Never load the redaction sentinel into an editable input — leave secret
    // fields blank; blank-on-save means "keep the stored value" (backend merge).
    if (SECRET_KEYS.includes(k)) {
      out[k] = '';
      continue;
    }
    out[k] = typeof v === 'string' ? v : String(v);
  }
  // The seeded channels carry `to` / `from`; the form writes `recipient` /
  // `smtp_from`. Without this the fields open blank on a seeded channel and the
  // first save wipes them.
  if (!out['recipient'] && out['to']) out['recipient'] = out['to'];
  if (!out['smtp_from'] && out['from']) out['smtp_from'] = out['from'];
  return out;
}

// A human, secret-free summary of a channel's config for the list column. The
// wireframe shows it as the channel's Destinație, so a channel that cannot name
// its recipient has to say so here rather than render an empty cell.
//
// `roleLabel` is the shared role resolver (`use-observer-roles`), passed in
// because it is a hook at the call site. An in-app channel's destination is a
// SIMSM role, and this column printed its code — SYSTEM_ADMIN, OBSERVER_MS —
// while every other screen that names a role already routes it through that
// resolver. It is required rather than defaulted: a default that returned the
// code would let the next call site reintroduce the defect without the compiler
// saying so.
export function channelConfigSummary(
  kind: string,
  config: Record<string, unknown>,
  t: (key: string) => string,
  roleLabel: (roleCode: string) => string,
): string {
  const has = (k: string): boolean => config[k] === '__set__';
  const str = (k: string): string => (typeof config[k] === 'string' ? (config[k] as string) : '');
  const secret = (k: string, key: string): string | null => (has(k) ? t(key) : null);
  switch (kind) {
    case 'webhook':
      return [
        str('url') || t('alerts.channels.summary.no_url'),
        secret('secret', 'alerts.channels.summary.secret_set'),
      ]
        .filter(Boolean)
        .join(' · ');
    case 'email':
      return [
        str('recipient') || str('to') || t('alerts.channels.summary.no_recipient'),
        [str('smtp_host'), str('smtp_port')].filter(Boolean).join(':'),
        str('smtp_from') || str('from'),
        secret('smtp_password', 'alerts.channels.summary.password_set'),
      ]
        .filter(Boolean)
        .join(' · ');
    case 'mnotify': {
      const label = str('recipient_label');
      const recipient = MNOTIFY_RECIPIENT_KEYS[label];
      return [
        recipient ? t(recipient) : t('alerts.channels.summary.no_recipient'),
        secret('api_key', 'alerts.channels.summary.api_key_set'),
      ]
        .filter(Boolean)
        .join(' · ');
    }
    case 'inapp': {
      const routeLabel = str('route_label');
      return routeLabel ? roleLabel(routeLabel) : t('alerts.channels.summary.no_route_label');
    }
    default:
      return EMPTY_CELL;
  }
}

// PATCH replaces the stored config wholesale, so every key the channel needs to
// deliver has to be listed here. `recipient` was missing: saving an e-mail
// channel dropped its recipients and delivery stopped without an error.
export function configToPayload(form: ChannelFormState): Record<string, unknown> {
  switch (form.kind) {
    case 'webhook':
      return {
        url: form.config['url'] ?? '',
        secret: form.config['secret'] ?? '',
      };
    case 'email':
      return {
        recipient: form.config['recipient'] ?? '',
        smtp_host: form.config['smtp_host'] ?? '',
        smtp_port: Number(form.config['smtp_port'] ?? 587),
        smtp_from: form.config['smtp_from'] ?? '',
        smtp_password: form.config['smtp_password'] ?? '',
      };
    case 'mnotify':
      return {
        recipient_label: form.config['recipient_label'] || MNOTIFY_RECIPIENT_LABELS[0],
        api_key: form.config['api_key'] ?? '',
      };
    case 'inapp':
    default:
      return { route_label: form.config['route_label'] ?? '' };
  }
}

export function NotificationChannelsPage(): JSX.Element {
  const { t } = useTranslate();
  const navigate = useNavigate();
  // The destination of an in-app channel is a SIMSM role; this names it.
  const roleLabel = useRoleLabel();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const toast = useToast();
  // The register offers no delete — the wireframe gives a channel Editează and
  // Inactivează/Activează and nothing else, because a deleted channel leaves its
  // uuid inside `notification_policy.channel_ids`, which has no foreign key. The
  // reversible half of that action is the active toggle, which is what is here.
  const [actionErrorKey, setActionErrorKey] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['notification-channels'],
    queryFn: () =>
      apiClient
        .get<{ channels: ChannelDto[] }>('/admin/notification-channels')
        .then((r) => r.data.channels),
  });

  // Read only so the deactivation confirmation can name the policies that stop
  // delivering through this channel (wireframe AdminAlerte.tsx:1625-1638).
  const policies = useQuery({
    queryKey: ['notification-policies'],
    queryFn: () =>
      apiClient
        .get<{
          policies: { id: string; name: string; displayName: string; channelIds: string[] }[];
        }>('/admin/notification-policies')
        .then((r) => r.data.policies),
  });

  // The row toggle sends the channel back whole, because PATCH replaces both the
  // config and the flag (CHANNEL_PARTIAL_UNSUPPORTED) — a toggle that sent only
  // `active` would blank the recipient and stop delivery in silence.
  const toggleActive = useMutation({
    mutationFn: (channel: ChannelDto) =>
      apiClient
        .patch<ChannelDto>(`/admin/notification-channels/${channel.id}`, {
          config: configToPayload({
            id: channel.id,
            name: channel.name,
            displayName: channel.displayName,
            kind: channel.kind,
            config: configFromDto(channel.config),
            active: channel.active,
          }),
          active: !channel.active,
        })
        .then((r) => r.data),
    onSuccess: (saved) => {
      setActionErrorKey(null);
      queryClient.invalidateQueries({ queryKey: ['notification-channels'] });
      queryClient.invalidateQueries({ queryKey: ['notification-policies'] });
      toast({
        variant: saved.active ? 'success' : 'info',
        title: saved.active
          ? t('alerts.toast.channel.activated')
          : t('alerts.toast.channel.deactivated'),
        text: saved.displayName,
        closeLabel: t('common.close'),
      });
    },
    onError: (e: unknown) =>
      setActionErrorKey(apiErrorKey(e, 'alerts.channels.error.toggle_failed')),
  });

  const rows = query.data ?? [];

  function editorHref(channelId: string): string {
    return `/system-admin/alert-channels/${channelId}/edit`;
  }

  // Deactivating stops delivery — for every policy that routes through the
  // channel — so it asks first and names them. Activating is additive.
  async function askThenToggle(channel: ChannelDto): Promise<void> {
    if (channel.active) {
      const affected = (policies.data ?? [])
        .filter((p) => p.channelIds.includes(channel.id))
        .map((p) => p.displayName || p.name);
      const confirmed = await confirm({
        title: t('alerts.confirm.deactivate_channel.title'),
        body: (
          <>
            <p>
              {t('alerts.confirm.deactivate_channel.body', {
                name: channel.displayName || channel.name,
              })}
            </p>
            {affected.length > 0 ? (
              <p className="simsm-alerts__meta">
                {t('alerts.confirm.deactivate_channel.affected', {
                  policies: affected.join(', '),
                })}
              </p>
            ) : null}
          </>
        ),
        confirmLabel: t('alerts.action.deactivate'),
        cancelLabel: t('alerts.confirm.action.cancel'),
        destructive: true,
      });
      if (!confirmed) return;
    }
    setActionErrorKey(null);
    toggleActive.mutate(channel);
  }

  // The wireframe's `tbl zebra compact … leading-actions-table` (AdminAlerte.tsx
  // :3727) as AGE's own table, with the shared leading row-actions track
  // (docs/ui-shape.md §Shared leading row-actions column), no visible heading.
  const columns: TableColumn[] = [
    { key: 'actions', label: '', width: '76px' },
    { key: 'channel', label: t('alerts.channels.column.channel') },
    { key: 'status', label: t('alerts.column.status'), width: '22%' },
  ];
  const tableRows: TableRowData[] = rows.map((ch) => ({ id: ch.id }));

  // An empty register keeps its table. The wireframe leaves the head, the
  // frame and the footer standing and puts the message in one centred cell
  // spanning every column (AdminAlerte.tsx:3589). Both QueryState branches
  // render this same node, because mud-table draws its `empty` slot on its own
  // once `rows` is [].
  const register = (
    <>
      <div className="simsm-alerts__register">
        <MudTable
          className="simsm-table"
          columns={columns}
          rows={tableRows}
          rowStyle="zebra"
          disableSort={true}
          aria-label={t('alerts.section.channels')}
          // The whole row opens the channel's editor, the menu's first entry.
          hoverable
          onMudRowClick={onRowOpen((index) => {
            const ch = rows[index];
            if (ch) navigate(editorHref(ch.id));
          })}
        >
          <EmptyRegisterMessage>{t('alerts.channels.empty')}</EmptyRegisterMessage>
          <span slot="header-cell-actions" className="visually-hidden">
            {t('alerts.column.actions')}
          </span>
          {rows.map((ch, index) => {
            const muted = ch.active ? '' : ' simsm-alerts__cell--muted';
            const summary = channelConfigSummary(ch.kind, ch.config, t, roleLabel);
            return [
              <span
                key={`actions-${ch.id}`}
                slot={`cell-actions-${index}`}
                className="simsm-alerts__cell simsm-alerts__cell--actions"
              >
                <RowActionsMenu
                  label={t('alerts.action.row_menu', { name: ch.displayName })}
                  actions={[
                    { label: t('alerts.action.edit'), onSelect: () => navigate(editorHref(ch.id)) },
                    {
                      label: ch.active
                        ? t('alerts.action.deactivate')
                        : t('alerts.action.activate'),
                      separator: true,
                      disabled: toggleActive.isPending,
                      onSelect: () => {
                        void askThenToggle(ch);
                      },
                    },
                  ]}
                />
              </span>,
              <span
                key={`channel-${ch.id}`}
                slot={`cell-channel-${index}`}
                className={`simsm-alerts__cell-name${muted}`}
              >
                <RouterLink to={editorHref(ch.id)} size="sm" variant="strict" underline="hover">
                  {ch.displayName}
                </RouterLink>
                <MetadataLine
                  wrap
                  items={[
                    { label: t('alerts.channels.column.kind'), value: t(kindLabelKey(ch.kind)) },
                    {
                      label: t('alerts.channels.column.destination'),
                      value: summary,
                      title: summary,
                    },
                  ]}
                />
              </span>,
              <span
                key={`status-${ch.id}`}
                slot={`cell-status-${index}`}
                className={`simsm-alerts__cell${muted}`}
              >
                <MudTag
                  variant="status"
                  type="outlined"
                  size="sm"
                  semantic={ch.active ? 'success' : 'neutral'}
                  // A canal is masculine: `alerts.status.*` is worded for a
                  // regulă ("Activă"), which is what the wireframe's own channel
                  // tag does not say.
                  label={
                    ch.active
                      ? t('alerts.channels.status.active')
                      : t('alerts.channels.status.inactive')
                  }
                />
              </span>,
            ];
          })}
        </MudTable>
      </div>
      {/* The register says how many rows it is showing, as the wireframe does on
          every one of its five tabs. */}
      <div className="simsm-alerts__table-footer">
        <span className="simsm-alerts__muted-text">
          {t(rows.length === 1 ? 'alerts.channels.count_one' : 'alerts.channels.count_other', {
            count: rows.length,
          })}
        </span>
      </div>
    </>
  );

  return (
    <section className="simsm-page simsm-alerts">
      <PageHeader
        title={t('alerts.section_title')}
        lede={t('alerts.section_lede')}
        actions={
          <>
            {/* The wireframe carries the definitions menu on every definition tab,
                not only the first: the export/import bundle covers all four object
                types whichever tab it is started from. */}
            <AlertDefinitionsMenu />
            <MudButton
              shape="circular"
              variant="primary"
              size="md"
              onClick={() => navigate('/system-admin/alert-channels/new')}
            >
              <MudIcon slot="icon-start" name="plus-large" size={16} />
              {t('alerts.channels.action.new')}
            </MudButton>
          </>
        }
      />

      <AlertsSectionNav />

      {actionErrorKey ? (
        <MudInfoBox
          emphasis="strong"
          role="alert"
          variant="error"
          closable
          closeLabel={t('common.close')}
          onMudClose={() => setActionErrorKey(null)}
        >
          {t(actionErrorKey)}
        </MudInfoBox>
      ) : null}

      <QueryState query={query} isEmpty={rows.length === 0} empty={register}>
        {register}
      </QueryState>
    </section>
  );
}
